// The `quickchat_backdrop` messages the widget sends its host, shared by the widget and the loader.
export const BACKDROP_MESSAGE = 'quickchat_backdrop';
export const BACKDROP_APPLIED_MESSAGE = 'quickchat_backdrop_applied';

/** Top-left, top-right, bottom-right, bottom-left, in `border-radius` order. */
export type CornerRadii = [number, number, number, number];

/** A glass surface the widget asks its host to blur, in iframe-local CSS px. */
export type BackdropShape = {
  // Which surface this is; absent from older widgets, which are keyed by position instead.
  id?: string;
  top: number;
  left: number;
  width: number;
  height: number;
  // One radius for every corner, for loaders that predate `radii`.
  radius: number;
  radii?: number[];
  // How far the surface has faded in; absent means fully shown.
  opacity?: number;
};

/** One end of a motion: the surface's box, and its ancestors' clip as insets (negative past its edge). */
export type MotionFrame = {
  top: number;
  left: number;
  width: number;
  height: number;
  radii: CornerRadii;
  inset: [number, number, number, number];
};

/** A surface's whole move, so the host's blur keeps pace with it instead of trailing by a frame. */
export type BackdropMotion = {
  id: string;
  from: MotionFrame;
  to: MotionFrame;
  // Epoch milliseconds, so the host can place the motion on its own clock.
  startTime: number;
  duration: number;
  easing: string;
};

export type BackdropViewport = { width: number; height: number };

export type BackdropPayload = {
  blur: number;
  shapes: BackdropShape[];
  // The frame size the shapes were measured in; the host skips them once its iframe has another.
  viewport?: BackdropViewport;
  motion?: BackdropMotion;
  // A surface whose handed-over motion no longer matches it: the per-frame shapes drive it again.
  endMotion?: string;
};

export const isBackdropShape = (shape: unknown): shape is BackdropShape =>
  !!shape &&
  typeof shape === 'object' &&
  (['top', 'left', 'width', 'height', 'radius'] as const).every((key) =>
    Number.isFinite((shape as Record<string, unknown>)[key]),
  );

export const isMotionFrame = (frame: unknown): frame is MotionFrame => {
  if (!frame || typeof frame !== 'object') return false;
  const { top, left, width, height, radii, inset } = frame as Record<string, unknown>;
  const corners = (value: unknown) =>
    Array.isArray(value) && value.length === 4 && value.every(Number.isFinite);
  return [top, left, width, height].every(Number.isFinite) && corners(radii) && corners(inset);
};

export const isBackdropMotion = (motion: unknown): motion is BackdropMotion => {
  if (!motion || typeof motion !== 'object') return false;
  const { id, from, to, startTime, duration, easing } = motion as Record<string, unknown>;
  return (
    typeof id === 'string' &&
    typeof easing === 'string' &&
    Number.isFinite(startTime) &&
    Number.isFinite(duration) &&
    (duration as number) > 0 &&
    isMotionFrame(from) &&
    isMotionFrame(to)
  );
};

export const readViewport = (viewport: unknown): BackdropViewport | null => {
  if (!viewport || typeof viewport !== 'object') return null;
  const { width, height } = viewport as Record<string, unknown>;
  return Number.isFinite(width) && Number.isFinite(height)
    ? { width: width as number, height: height as number }
    : null;
};

/** Whether this page can blur what sits behind an element. */
export const supportsBackdropFilter = (): boolean =>
  typeof CSS !== 'undefined' &&
  typeof CSS.supports === 'function' &&
  (CSS.supports('backdrop-filter', 'blur(1px)') ||
    CSS.supports('-webkit-backdrop-filter', 'blur(1px)'));

/** A shape's corners as a `border-radius` value; older widgets send one radius for all four. */
export const cornerRadii = (shape: BackdropShape): string => {
  const radii =
    Array.isArray(shape.radii) && shape.radii.length === 4 && shape.radii.every(Number.isFinite)
      ? shape.radii
      : [shape.radius];
  return radii.map((radius) => `${radius}px`).join(' ');
};

/** What a host draws, by surface: the measured shapes, plus a moving surface the widget has not measured yet. */
export const drawnShapes = (
  shapes: BackdropShape[],
  motion: BackdropMotion | null,
): Map<string, BackdropShape> => {
  const drawn = new Map<string, BackdropShape>();
  shapes.forEach((shape, index) => drawn.set(shape.id ?? `${index}`, shape));
  if (motion && !drawn.has(motion.id)) {
    const { top, left, width, height, radii } = motion.from;
    drawn.set(motion.id, { id: motion.id, top, left, width, height, radius: radii[0], radii });
  }
  return drawn;
};

/** The motion's start on this page's timeline; one in its future means the two clocks disagree, so it starts now. */
export const motionStartTime = (motion: BackdropMotion): number =>
  Math.min(motion.startTime - performance.timeOrigin, performance.now());
